import { footerHTML } from '../ui/Footer';
import { navHTML } from '../ui/Nav';

const PRIVACY_POLICY = `
<h1>Privacy Policy</h1>
<p><strong>Last updated:</strong> June 2026</p>

<h2>Who we are</h2>
<p>StonkRider is a free browser game operated by Aymeric Dietrich, based in Paris, France.</p>

<h2>What data we collect</h2>
<p>We collect <strong>anonymous usage analytics</strong> to improve the game. This includes:</p>
<ul>
  <li>Pages visited and actions taken (e.g. game started, game finished, ticker selected)</li>
  <li>Game statistics (score, crashes, flips, time played)</li>
  <li>General device information (browser type, screen size, country — derived from IP address)</li>
  <li>Leaderboard entries: nickname, score, time, and country (derived from IP at submission)</li>
</ul>

<h2>Early access waitlist</h2>
<p>If you sign up for the early access waitlist, we collect your <strong>email address</strong> and optional feature preferences. This data is stored in Cloudflare KV and used solely to notify you of product updates. You can request removal at any time by contacting us.</p>

<h2>What we do NOT collect</h2>
<ul>
  <li>No cookies or persistent identifiers — we use cookieless, session-only analytics</li>
  <li>No advertising trackers or third-party tracking pixels</li>
  <li>No financial data or trading information</li>
</ul>

<h2>Analytics provider</h2>
<p>We use <a href="https://posthog.com" target="_blank" rel="noopener">PostHog</a> (EU servers) for anonymous analytics. PostHog is configured in cookieless mode — no data is stored on your device between sessions. Your IP address is used to derive approximate location but is not stored.</p>

<h2>Third-party data sources</h2>
<p>Game terrain is generated from publicly available data:</p>
<ul>
  <li><strong>Stock data</strong> is sourced from financial APIs (Yahoo Finance). No user data is sent to these providers.</li>
  <li><strong>Startup revenue data</strong> is sourced from <a href="https://trustmrr.com" target="_blank" rel="noopener">TrustMRR</a>. Revenue figures are published by the startups themselves on TrustMRR. Our server fetches this data — no user data is transmitted to TrustMRR.</li>
</ul>

<h2>Data retention</h2>
<p>Anonymous analytics data is retained for up to 12 months, then automatically deleted. Leaderboard entries are retained indefinitely. Early access emails are retained until you request removal.</p>

<h2>Your rights</h2>
<p>If you submitted a leaderboard entry or signed up for early access, you can request access, modification, or deletion of your data by contacting us at <a href="mailto:oopsideployed@gmail.com">oopsideployed@gmail.com</a>.</p>

<h2>Changes</h2>
<p>We may update this policy from time to time. Changes will be reflected on this page with an updated date.</p>
`;

const TERMS_OF_USE = `
<h1>Terms of Use</h1>
<p><strong>Last updated:</strong> June 2026</p>

<h2>The game</h2>
<p>StonkRider is a free entertainment game. You ride a virtual motocross bike on terrain generated from real historical stock market data and startup revenue data. That's it — it's a game.</p>

<h2>Not financial advice</h2>
<p>StonkRider is <strong>not a financial product</strong>. The stock and revenue data displayed is historical and used solely to generate game terrain. Nothing on this site constitutes investment advice, financial guidance, or a recommendation to buy, sell, or invest in any security or startup. Do not make financial or investment decisions based on this game. Seriously.</p>

<h2>Market and revenue data</h2>
<p>Historical stock price data is sourced from publicly available financial APIs. Startup revenue data is sourced from <a href="https://trustmrr.com" target="_blank" rel="noopener">TrustMRR</a>, where startups voluntarily publish their revenue figures. We do not guarantee the accuracy, completeness, or timeliness of any data. Prices and revenue figures shown may be delayed or differ from actual values.</p>

<h2>Third-party content</h2>
<p>The game displays third-party content including startup names, logos, descriptions, and founder information sourced from TrustMRR. This content is displayed for entertainment purposes and remains the property of its respective owners. If you are a startup founder and wish to have your data removed from StonkRider, contact us and we will comply promptly.</p>

<h2>No account required</h2>
<p>You can play StonkRider without creating an account or providing any personal information.</p>

<h2>Acceptable use</h2>
<p>Don't abuse the service, attempt to overload our servers, or use automated tools to spam the game. We reserve the right to block abusive traffic.</p>

<h2>Intellectual property</h2>
<p>The StonkRider name, logo, and game design are the property of Aymeric Dietrich. Stock ticker symbols, company names, startup names, logos, and associated content are the property of their respective owners.</p>

<h2>Limitation of liability</h2>
<p>StonkRider is provided "as is" without warranties of any kind. We are not liable for any damages arising from your use of the game.</p>

<h2>Contact</h2>
<p>Questions? Reach out at <a href="mailto:oopsideployed@gmail.com">oopsideployed@gmail.com</a>.</p>
`;

const LEGAL_NOTICE = `
<h1>Legal Notice</h1>
<p><strong>Last updated:</strong> June 2026</p>

<h2>Publisher</h2>
<p>Aymeric Dietrich<br>Paris, France<br><a href="mailto:oopsideployed@gmail.com">oopsideployed@gmail.com</a></p>

<h2>Hosting</h2>
<p>This website is hosted by <a href="https://www.cloudflare.com" target="_blank" rel="noopener">Cloudflare, Inc.</a><br>101 Townsend Street, San Francisco, CA 94107, USA</p>

<h2>Domain</h2>
<p>stonkrider.com — registered via Squarespace Domains</p>
`;

const PAGES: Record<string, string> = {
  privacy: PRIVACY_POLICY,
  terms: TERMS_OF_USE,
  legal: LEGAL_NOTICE,
};

export function renderLegalPage(container: HTMLElement, page: string) {
  const content = PAGES[page] || PAGES.legal;

  container.innerHTML = `
    ${navHTML()}
    <main class="legal-content">
      <a href="#/" class="back-badge">&larr; Home</a>
      ${content}
    </main>
    ${footerHTML()}
  `;
}
